import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useUnreadMessages } from '../../../store/UnreadMessagesContext';
import { buildAuthHeaders } from '../../../services/authTokenStore';
import { resolveGatewayBackedServiceBaseUrl } from '../../../services/serviceBaseUrl';

const CHAT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);
const USER_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);
const RETRY_DELAY_MS = 2000;
const AUTH_FAILURE_STATUSES = new Set([401, 403]);

const buildAuthenticatedRequestConfig = () => ({
  withCredentials: true,
  headers: buildAuthHeaders(),
});

const isAuthFailure = (error) => AUTH_FAILURE_STATUSES.has(error?.response?.status);

export default function UnreadBootstrap() {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { setTotalUnread, setTotalGroupUnread, setPendingFriendRequests } = useUnreadMessages();
  const hasFetchedRef = useRef(false);
  const friendRequestsFetchedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const friendRequestsRetryRef = useRef(null);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const [chatRes, groupRes] = await Promise.all([
          axios.get(`${CHAT_SERVICE_URL}/api/chat/unread-total`, buildAuthenticatedRequestConfig()),
          axios.get(`${CHAT_SERVICE_URL}/api/chat/groups/unread-total`, buildAuthenticatedRequestConfig()),
        ]);
        setTotalUnread(Number(chatRes.data) || 0);
        setTotalGroupUnread(Number(groupRes.data) || 0);
      } catch (error) {
        if (isAuthFailure(error)) {
          setTotalUnread(0);
          setTotalGroupUnread(0);
          return;
        }
        hasFetchedRef.current = false;
        retryTimerRef.current = window.setTimeout(fetchUnread, RETRY_DELAY_MS);
      }
    };

    const fetchFriendRequests = async () => {
      try {
        const response = await axios.get(
          `${USER_SERVICE_URL}/api/users/me/friends/requests/incoming`,
          buildAuthenticatedRequestConfig(),
        );
        const count = Array.isArray(response.data) ? response.data.length : 0;
        setPendingFriendRequests(count);
      } catch (error) {
        if (isAuthFailure(error)) {
          setPendingFriendRequests(0);
          return;
        }
        friendRequestsFetchedRef.current = false;
        friendRequestsRetryRef.current = window.setTimeout(fetchFriendRequests, RETRY_DELAY_MS);
      }
    };

    if (!isLoggedIn) {
      hasFetchedRef.current = false;
      friendRequestsFetchedRef.current = false;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (friendRequestsRetryRef.current) {
        window.clearTimeout(friendRequestsRetryRef.current);
        friendRequestsRetryRef.current = null;
      }
      setTotalUnread(0);
      setTotalGroupUnread(0);
      setPendingFriendRequests(0);
      return undefined;
    }

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchUnread();
    }

    if (!friendRequestsFetchedRef.current) {
      friendRequestsFetchedRef.current = true;
      fetchFriendRequests();
    }

    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (friendRequestsRetryRef.current) {
        window.clearTimeout(friendRequestsRetryRef.current);
        friendRequestsRetryRef.current = null;
      }
    };
  }, [isLoggedIn, setPendingFriendRequests, setTotalGroupUnread, setTotalUnread]);

  return null;
}
